import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "./api";
import type {
  AdminApartado, AdminApartadoDetail, AdminOverview, AdminReservationRequest, AdminTrip, AdminTripDetail, AdminUserDetail, AdminUserSummary, Apartado,
  ApartadoEligibility, ApiEnvelope, AppSettings, AuditEvent, AuthSettings, KuspitCall, KuspitContract, KuspitNotification,
  KuspitNotificationStatus, KuspitOverview, LenderAccount, LendingOverview, LoanApplication, LoanConfig, LoanDocument, LoanStatus, MediaPhoto,
  PartnerLoanDetail, PartnerLoanSummary, PartnerOverview, Product, Region, ReservationConfig, ReservationRequest, RequestStatusGroup, SavedTrip, TripCard, TripPage, TripProfile, Wallet,
  AdminAnalytics, FundOverview, RequestCounts, FundWalletDetail, FundWalletRow, KycState, CatalogueOption, KycFile
} from "../types";

// Content the administrator edits is re-read on a short interval and whenever the tab regains focus,
// so an admin change shows up in an open customer session without a reload.
const CONTENT = { staleTime: 20_000, refetchInterval: 45_000 } as const;

/** `enabled: false` for guests browsing the catalogue: they have no wallets to read. */
export function useWallets({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["wallets"],
    enabled,
    queryFn: () => apiRequest<ApiEnvelope<{ wallets: Wallet[] }>>("/api/v1/wallets"),
    // A just-created wallet is `pending` while its Kuspit contract opens (slow, 30s+). Poll quickly
    // so it flips to `active` and its CLABE appears within seconds instead of after the idle minute.
    refetchInterval: (query) =>
      query.state.data?.data.wallets.some((wallet) => wallet.status === "pending") ? 5_000 : 60_000,
    select: (payload) => payload.data.wallets
  });
}

export function useLibreWallet(options: { enabled?: boolean } = {}) {
  const query = useWallets(options);
  return { ...query, wallet: query.data?.find((wallet) => wallet.product === "libre") };
}

/** Where the customer's KYC stands: the data Kuspit opens the contract with, and the file that lifts the 3,000 UDI cap. */
export function useKyc(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["kyc"],
    queryFn: () => apiRequest<ApiEnvelope<KycState>>("/api/v1/kyc"),
    enabled: options.enabled ?? true,
    select: (payload) => payload.data
  });
}

/** One of Kuspit's option lists for the KYC forms; they change rarely. */
export function useKycCatalogue(name: string, params: Record<string, string> = {}, enabled = true) {
  const query = new URLSearchParams(params).toString();
  return useQuery({
    queryKey: ["kyc", "catalogue", name, query],
    queryFn: () => apiRequest<ApiEnvelope<{ options: CatalogueOption[] }>>(`/api/v1/kyc/catalogues/${name}${query ? `?${query}` : ""}`),
    enabled,
    staleTime: 60 * 60_000,
    select: (payload) => payload.data.options
  });
}

/** Sends one file document (the raw file) to Kuspit through the API. */
export async function uploadKycDocument(kind: string, file: File, identification?: "ine" | "passport") {
  const params = new URLSearchParams({ fileName: file.name, ...(identification ? { identification } : {}) });
  const contentType = file.type === "application/pdf" || file.type === "image/png" || file.type === "image/jpeg" ? file.type : "application/octet-stream";
  return apiRequest<ApiEnvelope<{ accepted: boolean; needsBack: boolean; message: string | null; file: KycFile }>>(`/api/v1/kyc/file/documents/${kind}?${params}`, {
    method: "POST",
    headers: { "content-type": contentType },
    body: file
  });
}

export function useDiscover() {
  return useQuery({
    queryKey: ["content", "discover"],
    queryFn: () => apiRequest<ApiEnvelope<{ regions: Region[] }>>("/api/v1/content/discover"),
    ...CONTENT,
    select: (payload) => payload.data.regions
  });
}

export function useProducts() {
  return useQuery({
    queryKey: ["content", "products"],
    queryFn: () => apiRequest<ApiEnvelope<{ products: Product[]; settings: AppSettings }>>("/api/v1/content/products"),
    ...CONTENT,
    select: (payload) => payload.data
  });
}

export function useGallery() {
  return useQuery({
    queryKey: ["content", "gallery"],
    queryFn: () => apiRequest<ApiEnvelope<{ photos: MediaPhoto[]; auth: AuthSettings | null }>>("/api/v1/content/gallery", { retryAuth: false }),
    staleTime: 60_000,
    select: (payload) => payload.data
  });
}

export function useDreamPhotos() {
  return useQuery({
    queryKey: ["content", "dream-photos"],
    queryFn: () => apiRequest<ApiEnvelope<{ photos: MediaPhoto[] }>>("/api/v1/content/dream-photos"),
    staleTime: 30_000,
    select: (payload) => payload.data.photos
  });
}

export function useTrip() {
  return useQuery({
    queryKey: ["profile", "trip"],
    queryFn: () => apiRequest<ApiEnvelope<{ trip: TripProfile | null }>>("/api/v1/profile/trip"),
    ...CONTENT,
    select: (payload) => payload.data.trip
  });
}

// --- Travel Now · Pay Later -------------------------------------------------------------------
export function useLoanConfig({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["loans", "config"],
    enabled,
    queryFn: () => apiRequest<ApiEnvelope<LoanConfig>>("/api/v1/loans/config"),
    ...CONTENT,
    select: (payload) => payload.data
  });
}

export function useMyLoanApplications() {
  return useQuery({
    queryKey: ["loans", "applications"],
    queryFn: () => apiRequest<ApiEnvelope<{ applications: LoanApplication[] }>>("/api/v1/loans/applications"),
    staleTime: 15_000,
    refetchInterval: 60_000,
    select: (payload) => payload.data.applications
  });
}

export function useMyLoanApplication(id: string | undefined) {
  return useQuery({
    queryKey: ["loans", "application", id],
    queryFn: () => apiRequest<ApiEnvelope<{ application: LoanApplication }>>(`/api/v1/loans/applications/${id}`),
    enabled: Boolean(id),
    select: (payload) => payload.data.application
  });
}

/** Uploads the Constancia de Situación Fiscal as the raw request body; the API checks it is a PDF. */
export async function uploadLoanDocument(file: File): Promise<LoanDocument> {
  const payload = await apiRequest<ApiEnvelope<{ document: LoanDocument }>>(`/api/v1/loans/documents?name=${encodeURIComponent(file.name)}`, {
    method: "POST",
    headers: { "content-type": "application/pdf" },
    body: file
  });
  return payload.data.document;
}

export async function deleteLoanDocument(id: string): Promise<void> {
  await apiRequest<void>(`/api/v1/loans/documents/${id}`, { method: "DELETE" });
}

// --- Lending partner (Peso a Peso) -------------------------------------------------------------
export function usePartnerOverview() {
  return useQuery({
    queryKey: ["partner", "overview"],
    queryFn: () => apiRequest<ApiEnvelope<PartnerOverview>>("/api/v1/partner/overview"),
    refetchInterval: 30_000,
    select: (payload) => payload.data
  });
}

export function usePartnerApplications(search: string, status: LoanStatus | "") {
  const params = new URLSearchParams();
  if (search) params.set("q", search);
  if (status) params.set("status", status);
  return useQuery({
    queryKey: ["partner", "applications", search, status],
    queryFn: () => apiRequest<ApiEnvelope<{ applications: PartnerLoanSummary[] }>>(`/api/v1/partner/applications?${params.toString()}`),
    refetchInterval: 30_000,
    select: (payload) => payload.data.applications
  });
}

export function usePartnerApplication(id: string | undefined) {
  return useQuery({
    queryKey: ["partner", "application", id],
    queryFn: () => apiRequest<ApiEnvelope<PartnerLoanDetail>>(`/api/v1/partner/applications/${id}`),
    enabled: Boolean(id),
    select: (payload) => payload.data
  });
}

export function useAdminLenders() {
  return useQuery({
    queryKey: ["admin", "lenders"],
    queryFn: () => apiRequest<ApiEnvelope<{ lenders: LenderAccount[] }>>("/api/v1/admin/lenders"),
    select: (payload) => payload.data.lenders
  });
}

// --- Administration ---------------------------------------------------------------------------
export function useAdminOverview() {
  return useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => apiRequest<ApiEnvelope<AdminOverview>>("/api/v1/admin/overview"),
    refetchInterval: 30_000,
    select: (payload) => payload.data
  });
}

export function useAdminUsers(search: string, limit = 200) {
  return useQuery({
    queryKey: ["admin", "users", search, limit],
    queryFn: () => apiRequest<ApiEnvelope<{ users: AdminUserSummary[] }>>(`/api/v1/admin/users?q=${encodeURIComponent(search)}&limit=${limit}`),
    select: (payload) => payload.data.users
  });
}

/** Builds a query string, dropping empty values so the cache key stays stable. */
function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value !== undefined && value !== "") search.set(key, String(value));
  const text = search.toString();
  return text ? `?${text}` : "";
}

// --- Kuspit operations panel ---------------------------------------------------------------------
export function useKuspitOverview() {
  return useQuery({
    queryKey: ["admin", "kuspit", "overview"],
    queryFn: () => apiRequest<ApiEnvelope<KuspitOverview>>("/api/v1/admin/kuspit/overview"),
    refetchInterval: 30_000,
    select: (payload) => payload.data
  });
}

export function useKuspitContracts(filters: { q?: string; status?: string; only?: string }) {
  return useQuery({
    queryKey: ["admin", "kuspit", "contracts", filters],
    queryFn: () => apiRequest<ApiEnvelope<{ contracts: KuspitContract[] }>>(`/api/v1/admin/kuspit/contracts${qs(filters)}`),
    select: (payload) => payload.data.contracts
  });
}

export function useKuspitNotifications(filters: { status?: KuspitNotificationStatus | ""; event?: string; q?: string }) {
  return useQuery({
    queryKey: ["admin", "kuspit", "notifications", filters],
    queryFn: () => apiRequest<ApiEnvelope<{ notifications: KuspitNotification[] }>>(`/api/v1/admin/kuspit/notifications${qs(filters)}`),
    refetchInterval: 30_000,
    select: (payload) => payload.data.notifications
  });
}

export function useKuspitCalls(filters: { result?: "ok" | "error" | ""; endpoint?: string }) {
  return useQuery({
    queryKey: ["admin", "kuspit", "calls", filters],
    queryFn: () => apiRequest<ApiEnvelope<{ calls: KuspitCall[] }>>(`/api/v1/admin/kuspit/calls${qs({ ...filters, limit: 150 })}`),
    refetchInterval: 30_000,
    select: (payload) => payload.data.calls
  });
}

// --- Fund sub-ledger (Fondos) --------------------------------------------------------------------
export function useFundOverview() {
  return useQuery({
    queryKey: ["admin", "fund", "overview"],
    queryFn: () => apiRequest<ApiEnvelope<FundOverview>>("/api/v1/admin/fondos/overview"),
    refetchInterval: 30_000,
    select: (payload) => payload.data
  });
}

export function useFundWallets(filters: { q?: string; solo?: string }) {
  return useQuery({
    queryKey: ["admin", "fund", "wallets", filters],
    queryFn: () => apiRequest<ApiEnvelope<{ wallets: FundWalletRow[] }>>(`/api/v1/admin/fondos/wallets${qs(filters)}`),
    refetchInterval: 30_000,
    select: (payload) => payload.data.wallets
  });
}

export function useFundWallet(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "fund", "wallet", id],
    queryFn: () => apiRequest<ApiEnvelope<FundWalletDetail>>(`/api/v1/admin/fondos/wallets/${id}`),
    enabled: Boolean(id),
    select: (payload) => payload.data
  });
}

// --- Peso a Peso panel ---------------------------------------------------------------------------
export function useLendingOverview() {
  return useQuery({
    queryKey: ["admin", "lending", "overview"],
    queryFn: () => apiRequest<ApiEnvelope<LendingOverview>>("/api/v1/admin/lending/overview"),
    refetchInterval: 30_000,
    select: (payload) => payload.data
  });
}

export function useAdminUser(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "user", id],
    queryFn: () => apiRequest<ApiEnvelope<AdminUserDetail>>(`/api/v1/admin/users/${id}`),
    enabled: Boolean(id),
    select: (payload) => payload.data
  });
}

export function useAdminMedia(kind?: string, includeCustomer = false) {
  const params = new URLSearchParams();
  if (kind) params.set("kind", kind);
  if (includeCustomer) params.set("includeCustomer", "true");
  return useQuery({
    queryKey: ["admin", "media", kind ?? "all", includeCustomer],
    queryFn: () => apiRequest<ApiEnvelope<{ photos: MediaPhoto[] }>>(`/api/v1/admin/media?${params.toString()}`),
    select: (payload) => payload.data.photos
  });
}

export function useAdminRegions() {
  return useQuery({
    queryKey: ["admin", "regions"],
    queryFn: () => apiRequest<ApiEnvelope<{ regions: Region[] }>>("/api/v1/admin/regions"),
    select: (payload) => payload.data.regions
  });
}

export function useAdminProducts() {
  return useQuery({
    queryKey: ["admin", "products"],
    queryFn: () => apiRequest<ApiEnvelope<{ products: Product[]; settings: AppSettings }>>("/api/v1/admin/products"),
    select: (payload) => payload.data
  });
}

export function useAdminAudit(limit = 50) {
  return useQuery({
    queryKey: ["admin", "audit", limit],
    queryFn: () => apiRequest<ApiEnvelope<{ events: AuditEvent[] }>>(`/api/v1/admin/audit?limit=${limit}`),
    refetchInterval: 30_000,
    select: (payload) => payload.data.events
  });
}

/** After an admin write, every customer-facing and admin query is refreshed. */
export function useInvalidateAll() {
  const client = useQueryClient();
  return () => Promise.all([
    client.invalidateQueries({ queryKey: ["admin"] }),
    client.invalidateQueries({ queryKey: ["content"] }),
    client.invalidateQueries({ queryKey: ["wallets"] }),
    client.invalidateQueries({ queryKey: ["profile"] }),
    client.invalidateQueries({ queryKey: ["loans"] }),
    client.invalidateQueries({ queryKey: ["partner"] }),
    client.invalidateQueries({ queryKey: ["trips"] }),
    client.invalidateQueries({ queryKey: ["reservations"] }),
    client.invalidateQueries({ queryKey: ["apartados"] })
  ]);
}

// --- Trips (Explore cards and trip pages) ---------------------------------------------------------
export function useTrips() {
  return useQuery({
    queryKey: ["trips", "list"],
    queryFn: () => apiRequest<ApiEnvelope<{ trips: TripCard[] }>>("/api/v1/trips"),
    ...CONTENT,
    select: (payload) => payload.data.trips
  });
}

export function useTripPage(slug: string | undefined, preview = false) {
  return useQuery({
    queryKey: ["trips", "page", slug, preview],
    queryFn: () => apiRequest<ApiEnvelope<{ trip: TripPage }>>(`/api/v1/trips/${encodeURIComponent(slug ?? "")}${preview ? "?preview=1" : ""}`),
    enabled: Boolean(slug),
    staleTime: 20_000,
    select: (payload) => payload.data.trip
  });
}

export function useReservationConfig() {
  return useQuery({
    queryKey: ["reservations", "config"],
    queryFn: () => apiRequest<ApiEnvelope<ReservationConfig>>("/api/v1/reservations/config"),
    staleTime: 60_000,
    select: (payload) => payload.data
  });
}

// --- Mis solicitudes ----------------------------------------------------------------------------

export function useMyRequests() {
  return useQuery({
    queryKey: ["reservations", "mine"],
    queryFn: () => apiRequest<ApiEnvelope<{ requests: ReservationRequest[]; counts: RequestCounts }>>("/api/v1/reservations"),
    staleTime: 10_000,
    refetchInterval: 60_000,
    select: (payload) => payload.data
  });
}

export function useMyRequest(id: string | undefined) {
  return useQuery({
    queryKey: ["reservations", "detail", id],
    queryFn: () => apiRequest<ApiEnvelope<{ request: ReservationRequest; apartadoEligibility: ApartadoEligibility }>>(`/api/v1/reservations/${id}`),
    enabled: Boolean(id),
    staleTime: 5_000,
    select: (payload) => payload.data
  });
}

export async function checkApartadoEligibility(requestId: string): Promise<ApartadoEligibility> {
  const payload = await apiRequest<ApiEnvelope<{ eligibility: ApartadoEligibility }>>(`/api/v1/reservations/${requestId}/apartado-eligibility`);
  return payload.data.eligibility;
}

// --- Apartados ----------------------------------------------------------------------------------
export function useMyApartados() {
  return useQuery({
    queryKey: ["apartados", "mine"],
    queryFn: () => apiRequest<ApiEnvelope<{ apartados: Apartado[] }>>("/api/v1/apartados"),
    staleTime: 10_000,
    select: (payload) => payload.data.apartados
  });
}

export function useApartado(id: string | undefined) {
  return useQuery({
    queryKey: ["apartados", "detail", id],
    queryFn: () => apiRequest<ApiEnvelope<{ apartado: Apartado }>>(`/api/v1/apartados/${id}`),
    enabled: Boolean(id),
    staleTime: 5_000,
    select: (payload) => payload.data.apartado
  });
}

// --- Viajes guardados ---------------------------------------------------------------------------------
export function useSavedTrips({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["saved-trips"],
    enabled,
    queryFn: () => apiRequest<ApiEnvelope<{ saved: SavedTrip[] }>>("/api/v1/saved-trips"),
    staleTime: 20_000,
    select: (payload) => payload.data.saved
  });
}

/** Save / unsave with an optimistic heart: the list is patched at once and re-read afterwards. */
export function useToggleSavedTrip() {
  const client = useQueryClient();
  return async (trip: TripCard, saved: boolean, placement: string): Promise<void> => {
    const key = ["saved-trips"];
    const previous = client.getQueryData<ApiEnvelope<{ saved: SavedTrip[] }>>(key);
    client.setQueryData<ApiEnvelope<{ saved: SavedTrip[] }>>(key, (current) => {
      const list = current?.data.saved ?? [];
      const next = saved
        ? list.some((item) => item.tripId === trip.id) ? list : [{ tripId: trip.id, priceAlert: false, savedPrice: trip.priceFrom ? { amount: trip.priceFrom.amount, currency: trip.priceFrom.currency } : null, savedAt: new Date().toISOString(), trip, priceChange: null, apartado: null, request: null }, ...list]
        : list.filter((item) => item.tripId !== trip.id);
      return { data: { saved: next } };
    });
    try {
      if (saved) await apiRequest(`/api/v1/saved-trips/${trip.id}`, { method: "PUT", body: JSON.stringify({ placement }) });
      else await apiRequest(`/api/v1/saved-trips/${trip.id}`, { method: "DELETE" });
    } catch (error) {
      client.setQueryData(key, previous);
      throw error;
    } finally {
      void client.invalidateQueries({ queryKey: key });
    }
  };
}

export async function setSavedTripAlert(tripId: string, priceAlert: boolean): Promise<void> {
  await apiRequest(`/api/v1/saved-trips/${tripId}`, { method: "PATCH", body: JSON.stringify({ priceAlert }) });
}

export async function setAllSavedTripAlerts(priceAlert: boolean): Promise<void> {
  await apiRequest("/api/v1/saved-trips/alerts", { method: "POST", body: JSON.stringify({ priceAlert }) });
}

export function useAdminAnalytics(days: number, name?: string) {
  return useQuery({
    queryKey: ["admin", "analytics", days, name ?? ""],
    queryFn: () => apiRequest<ApiEnvelope<AdminAnalytics>>(`/api/v1/admin/analytics${qs({ days, name })}`),
    refetchInterval: 60_000,
    select: (payload) => payload.data
  });
}

// --- Administration: trips, requests, Apartados -----------------------------------------------------
export function useAdminTrips() {
  return useQuery({
    queryKey: ["admin", "trips"],
    queryFn: () => apiRequest<ApiEnvelope<import("../types").AdminTripsIndex>>("/api/v1/admin/trips"),
    select: (payload) => payload.data
  });
}

export function useAdminTrip(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "trip", id],
    queryFn: () => apiRequest<ApiEnvelope<AdminTripDetail>>(`/api/v1/admin/trips/${id}`),
    enabled: Boolean(id),
    select: (payload) => payload.data
  });
}

/** The rate-card history of a trip: published pricing snapshots, their diffs and statistics. */
export function useAdminTripPricing(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "trip", id, "pricing"],
    queryFn: () => apiRequest<ApiEnvelope<import("../types").TripPricingHistory>>(`/api/v1/admin/trips/${id}/pricing`),
    enabled: Boolean(id),
    select: (payload) => payload.data
  });
}

/** Uploads a Word itinerary as the raw request body; the API extracts it into a draft. */
export async function uploadTripDocument(file: File, tripId?: string): Promise<AdminTrip & { content: import("../types").TripContent }> {
  const path = tripId ? `/api/v1/admin/trips/${tripId}/upload` : "/api/v1/admin/trips/upload";
  const payload = await apiRequest<ApiEnvelope<{ trip: AdminTrip & { content: import("../types").TripContent } }>>(`${path}?name=${encodeURIComponent(file.name)}`, {
    method: "POST",
    headers: { "content-type": "application/vnd.openxmlformats-officedocument.wordprocessingml.document" },
    body: file
  });
  return payload.data.trip;
}

export function useAdminReservations(filters: { q?: string; status?: string; kind?: string }) {
  return useQuery({
    queryKey: ["admin", "reservations", filters],
    queryFn: () => apiRequest<ApiEnvelope<{ requests: AdminReservationRequest[]; counts: Record<string, number> }>>(`/api/v1/admin/reservations${qs(filters)}`),
    refetchInterval: 30_000,
    select: (payload) => payload.data
  });
}

export function useAdminReservation(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "reservation", id],
    queryFn: () => apiRequest<ApiEnvelope<{ request: AdminReservationRequest; audit: AuditEvent[] }>>(`/api/v1/admin/reservations/${id}`),
    enabled: Boolean(id),
    select: (payload) => payload.data
  });
}

export function useAdminApartados(filters: { q?: string; status?: string }) {
  return useQuery({
    queryKey: ["admin", "apartados", filters],
    queryFn: () => apiRequest<ApiEnvelope<{ apartados: AdminApartado[]; totals: Record<string, { count: number; balance: number }> }>>(`/api/v1/admin/apartados${qs(filters)}`),
    refetchInterval: 30_000,
    select: (payload) => payload.data
  });
}

export function useAdminApartado(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "apartado", id],
    queryFn: () => apiRequest<ApiEnvelope<AdminApartadoDetail>>(`/api/v1/admin/apartados/${id}`),
    enabled: Boolean(id),
    select: (payload) => payload.data
  });
}

export type { RequestCounts, RequestStatusGroup };

/** Uploads one image as the raw request body; the API sniffs the real type. */
export async function uploadPhoto(file: File, options: { admin?: boolean; kind?: string; title?: string; sortOrder?: number } = {}) {
  const params = new URLSearchParams();
  if (options.kind) params.set("kind", options.kind);
  if (options.title) params.set("title", options.title);
  if (options.sortOrder !== undefined) params.set("sortOrder", String(options.sortOrder));
  const path = `${options.admin ? "/api/v1/admin/media" : "/api/v1/media"}?${params.toString()}`;
  const payload = await apiRequest<ApiEnvelope<{ photo: MediaPhoto }>>(path, {
    method: "POST",
    headers: { "content-type": file.type && file.type.startsWith("image/") ? file.type : "application/octet-stream" },
    body: file
  });
  return payload.data.photo;
}
